/**
 * ToggleSwitch — a compact label + native checkbox styled as a pill
 * toggle. Used across edit forms for `is_active` / `is_free` /
 * `is_new` and other boolean settings.
 *
 *   <ToggleSwitch
 *     name="session_is_active"
 *     checked={!!formData.session_is_active}
 *     onChange={(e) => setFormData({...formData, session_is_active: e.target.checked ? 1 : 0})}
 *     label="Active"
 *     hint="Hidden from clients while off"
 *   />
 *
 * Visual recipe (matches the prototype's `.field-toggle` pattern):
 *   • 36×20 pill track: sand when off → teal-green when on
 *   • 16×16 white knob with subtle drop shadow, slides left ↔ right
 *   • Label sits beside, Asap 13/600 navy
 *   • Optional hint inline-muted to the right of the label
 *   • Visually-hidden native <input type="checkbox"> for a11y + form
 *     submission — keeps `name=` semantics so the surrounding form
 *     state machinery doesn't need a special path
 */
import React from 'react';
import { cn } from '../cn';

export default function ToggleSwitch({
  checked,
  onChange,
  name,
  label,
  hint,
  disabled,
  className,
}) {
  return (
    <label
      className={cn(
        'inline-flex items-center cursor-pointer select-none',
        disabled && 'opacity-50 cursor-not-allowed',
        className,
      )}
      style={{ gap: 10, minWidth: 0 }}
    >
      {/* Track + knob. The native checkbox is visually-hidden but
       *  still part of the label, so click → toggle works without
       *  extra JS wiring. */}
      <span
        aria-hidden
        style={{
          position: 'relative',
          width: 36,
          height: 20,
          flexShrink: 0,
          borderRadius: 999,
          background: checked ? 'rgb(var(--color-teal-green))' /* teal-green */ : 'rgb(var(--color-sand))' /* sand */,
          transition: 'background 160ms ease',
        }}
      >
        <span
          style={{
            position: 'absolute',
            top: 2,
            left: checked ? 18 : 2,
            width: 16,
            height: 16,
            borderRadius: '50%',
            background: '#FFFFFF',
            boxShadow: '0 1px 3px #2C3E5040',
            transition: 'left 160ms ease',
          }}
        />
      </span>
      <input
        type="checkbox"
        name={name}
        checked={!!checked}
        onChange={onChange}
        disabled={disabled}
        style={{
          // Visually hidden but accessible.
          position: 'absolute',
          width: 1,
          height: 1,
          padding: 0,
          margin: -1,
          overflow: 'hidden',
          clip: 'rect(0, 0, 0, 0)',
          whiteSpace: 'nowrap',
          border: 0,
        }}
      />
      {label && (
        <span className="text-navy" style={{ fontSize: 13, fontWeight: 600 }}>
          {label}
          {hint && (
            <span
              className="text-text-muted"
              style={{ marginLeft: 6, fontWeight: 400, fontSize: 11.5 }}
            >
              {hint}
            </span>
          )}
        </span>
      )}
    </label>
  );
}
